import "./SamplesFiltersComponent.scss";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import {Field, FieldProps, Form, Formik, FormikProps, FormikValues} from "formik";
import CommonService from "../../../shared/services/common.service";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import FormikAutoCompleteDropdown
    from "../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormikDatePickerComponent from "../../../shared/components/core/formik-date-picker/FormikDatePickerComponent";
import formValidationRules from "../../../shared/services/validation.service";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import * as Yup from "yup";
import commonService from "../../../shared/services/common.service";

interface SamplesFiltersComponentProps {
    filtersData: any;
    onFiltersApply: (filterData: any) => void;
    clearAllFilters: () => void;
}

const validationSchema = Yup.object().shape({
    startDate: Yup.mixed().nullable().when("endDate", {
        is: (endDate: Date) => {
            return Boolean(endDate)
        },
        then: Yup.mixed().nullable().required("Start date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    }),
    endDate: Yup.mixed().nullable().when("startDate", {
        is: (startDate: Date) => {
            return Boolean(startDate)
        },
        then: Yup.mixed().nullable().required("End date is required"),
        otherwise: Yup.mixed().nullable().notRequired()
    })
}, [['startDate', 'endDate']]);


const SamplesFiltersComponent = (props: SamplesFiltersComponentProps) => {

    const {filtersData, onFiltersApply, clearAllFilters} = props;
    const [dataFiltersInitialState, setDataFiltersInitialState] = useState(filtersData);

    const {
        // sampleTypesList,
        sampleTypeWithCollectionDevices,
        sampleStatusList,
        testListLite,
        billingModeList,
        facilityListLite,
        allLocationListLite
    } = useSelector((state: IRootReducerState) => state.staticData);
    const formikRef = useRef<FormikProps<FormikValues>>(null);

    const onFiltersApplyHandler = useCallback((values: any) => {
        const payload = {...values};
        payload.facilityIds = payload.facilityObjs.map((item: any) => item.id);
        payload.locationIds = payload.locationObjs.map((item: any) => item.id);
        payload.testIds = payload.testCodeObjs.map((item: any) => item.id);
        payload.sampleTypes = payload.sampleTypesObjs.map((item: any) => item.sampleType);
        payload.billingModes = payload.billingModeObjs.map((item: any) => item.code);
        payload.statuses = payload.statusesObjs.map((item: any) => item.code);
        payload.startDate = payload.startDate ? CommonService.convertDateFormat(values['startDate'], 'YYYY-MM-DD') : '';
        payload.endDate = payload.endDate ? CommonService.convertDateFormat(values['endDate'], 'YYYY-MM-DD') : '';
        onFiltersApply(payload);
    }, [onFiltersApply]);

    useEffect(() => {
        setDataFiltersInitialState(filtersData);
    }, [filtersData]);

    return (
        <div className={'test-panel-filters-component screen t-form-screen'}>
            <>
                <div className="drawer-sub-header horizontal-vertical-center-align">
                    <HeadingComponent title={"Filters"}/>
                    <div className={'filters-clear-all-button'}
                         onClick={() => {
                             formikRef.current && formikRef.current.resetForm();
                             clearAllFilters();
                         }}
                    >
                        Clear all
                    </div>
                </div>
                <div className="t-form t-form-drawer">
                    <Formik
                        initialValues={dataFiltersInitialState}
                        enableReinitialize={true}
                        onSubmit={onFiltersApplyHandler}
                        innerRef={formikRef}
                        validationSchema={validationSchema}
                    >
                        {({isSubmitting, values, isValid, setFieldValue, validateForm, errors, dirty}) => {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            useEffect(() => {
                                validateForm();
                            }, [values, errors, validateForm]);

                            return (
                                <Form noValidate={true} className={"patient-filters-holder"}>
                                    <div className="t-form-controls-wrapper">
                                        <Field name={'facilityObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Facility Name'}
                                                        multiple={true}
                                                        data={facilityListLite}
                                                        valueKey={"id"}
                                                        placeholder={'Select Facility Name'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'locationObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Location Name'}
                                                        multiple={true}
                                                        data={allLocationListLite}
                                                        valueKey={"id"}
                                                        placeholder={'Select Location Name'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'sampleTypesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Sample Types'}
                                                        multiple={true}
                                                        data={sampleTypeWithCollectionDevices}
                                                        valueKey={"id"}
                                                        placeholder={'Select Sample Types'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (commonService.capitalizeFirstLetter(option.sampleType)) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'statusesObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Select Sample Status'}
                                                        multiple={true}
                                                        data={sampleStatusList}
                                                        valueKey={"code"}
                                                        placeholder={'Select Sample Status'}
                                                        formikField={field}
                                                        getOptionLabel={(option) => (option.title)}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'testCodeObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Select Test'}
                                                        multiple={true}
                                                        data={testListLite}
                                                        valueKey={"id"}
                                                        placeholder={'Select Test'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.code + " - " + option.name) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <Field name={'billingModeObjs'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Billing Mode'}
                                                        multiple={true}
                                                        data={billingModeList}
                                                        valueKey={"id"}
                                                        placeholder={'Select Billing Mode'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.title) || '')}
                                                    />
                                                )
                                            }
                                        </Field>

                                        <div className="filters-sub-header">Date Range</div>
                                        <Field name={'startDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'Start Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                        <Field name={'endDate'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikDatePickerComponent
                                                        label={'End Date'}
                                                        formikField={field}
                                                        placeholder={'Select End Date'}
                                                        maxDate={formValidationRules.today}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </div>
                                    <div className="drawer-actions form-action-section">
                                        <ButtonComponent variant={"contained"}
                                                         fullWidth={true}
                                                         type={"submit"}
                                                         disabled={!isValid || !dirty}
                                        >
                                            Apply
                                        </ButtonComponent>
                                    </div>
                                </Form>
                            )
                        }
                        }
                    </Formik>
                </div>
            </>
        </div>
    );

};

export default SamplesFiltersComponent;
